export type ReportTaxonomyDataType = 'string' | 'boolean' | 'number' | 'date' | 'duration';

export interface ReportTaxonomySort {
	field: string;
	direction: 'ASC' | 'DESC';
}

export interface ReportTaxonomyField {
	key: string;
	label: string;
	dataType: ReportTaxonomyDataType;
	categorical: boolean;
	filterable: boolean;
	sortable: boolean;
	groupable: boolean;
	operators: string[];
	/** The vocabulary a categorical field's value is drawn from, as the wire spells it - `RUNNING`,
	 * `FIXED_PRICE`, `Green`. Absent for a field with no closed value set. What a reader is SHOWN for
	 * one of these is a display decision that does not belong to the contract: the wire value is what
	 * a filter condition has to carry, so the two can legitimately differ.
	 *
	 * Its presence, not the data type, is also what says a value picker can be built from the catalog
	 * alone - see `resolverKindFor`'s `enumeratedValues` kind. Absent on every field whose values are
	 * ids resolved as live entities. Optional because most fields do not carry it. */
	values?: string[];
	/** Names the condition under which this field has no value to show. Null on every project-status
	 * field since catalog 1.2: a retainer column is emptied by any of four independent reasons, which
	 * one string cannot say, so the vocabulary moved to the catalog's `notApplicableRules` and WHICH
	 * reason fired travels on the row. Still carried by the task catalog, whose two uses
	 * (`project.deliverablesDisabled`, `project.sprintsDisabled`) really are single static
	 * conditions. */
	notApplicableRule?: string | null;
	/** True when the field's unit is per-row rather than fixed for the grain (a task estimate is
	 * hours or points depending on its project; a project's logged hours are always hours). */
	unitVaries?: boolean;
	/** True for a company-defined custom field injected into the catalog, which is groupable but
	 * neither filterable nor sortable - the upstream filter vocabulary cannot address one. */
	custom?: boolean;
	/** Null for a field that cannot be expressed as a filter condition at all - 15 fields in the
	 * task catalog carry it, and `fieldByFilterField`/`ConditionsEditor`/`addCondition` all guard on
	 * it. Declaring it non-null made those guards look redundant to the type checker while the
	 * vendored taxonomy really does ship nulls. */
	filterField: string | null;
	context: string;
	unit: string | null;
}

export interface ReportTaxonomy {
	reportType: string;
	version: string;
	fields: ReportTaxonomyField[];
	operators: {value: string; label: string}[];
	valueResolvers: Record<string, string>;
	contexts: {value: string; label: string}[];
	/** The field paths a report of this grain shows when nobody has chosen otherwise, in order. */
	defaultColumns: string[];
	/** The column a report of this grain sorts by when its definition names none. */
	defaultSort: ReportTaxonomySort | null;
	/** The grouping tokens this grain can be grouped by. The two grains do not share a vocabulary -
	 * a task report groups by phase and assignee, a project-status report by stage and RAG - so this
	 * is the only correct source for what the Group By control may offer, and for which token a
	 * stored definition is allowed to carry. */
	groupingValues: string[];
	/** Every reason a field on this grain can have no value to show, as tokens for this client to
	 * render in its own words - the same relationship a field's `values` has to what a reader sees.
	 * Which reason actually applied to a given row travels on the row, not here; this is the closed
	 * set of what that leaf may carry. Empty for a grain that publishes none. */
	notApplicableRules: string[];
}

/** Parses the taxonomy JSON the graphql proxy returns.
 *
 * Every key is defaulted rather than trusted: the taxonomy is published by report-service and can
 * legitimately be a version ahead or behind this client, and a missing key must degrade the surface
 * that reads it rather than throw on the way in. */
export function parseReportTaxonomy(json: string): ReportTaxonomy {
	const raw = JSON.parse(json);
	return {
		reportType: raw.reportType,
		version: raw.version,
		fields: raw.fields ?? [],
		operators: raw.operators ?? [],
		valueResolvers: raw.valueResolvers ?? {},
		contexts: raw.contexts ?? [],
		defaultColumns: raw.defaultColumns ?? [],
		defaultSort: raw.defaultSort ?? null,
		groupingValues: raw.groupingValues ?? [],
		notApplicableRules: raw.notApplicableRules ?? [],
	};
}

/** The catalog keys whose emptiness the grain-level `notApplicableRules` explain, in the order the
 * columns read.
 *
 * The catalog publishes the closed set of REASONS but not which fields they empty, so that half of
 * the relationship is spelled here - once, for both consumers: the row mapper, which moves a row's
 * reason onto exactly these cells, and `mayBeNotApplicable`, which decides whether a field can be
 * safely excluded on. Two hand-written copies would drift, and a drift is silent in both directions
 * (a cell that never gets its reason, or a negation offered on a field that cannot take one).
 *
 * Mirrors report-service's `ProjectStatusForcedPaths.RETAINER_PATHS`, which is the same six paths
 * from the emitting side. */
export const ROW_REASON_NOT_APPLICABLE_KEYS: readonly string[] = [
	'projectStatus.retainer.periodStart',
	'projectStatus.retainer.periodEnd',
	'projectStatus.retainer.target',
	'projectStatus.retainer.consumed',
	'projectStatus.retainer.remaining',
	'projectStatus.retainer.overUnder',
];

/** Whether a field can have no value to show on a given row, from either place the catalog says so.
 *
 * Two vocabularies, because catalog 1.2 moved the project-status grain from one to the other. The
 * per-field `notApplicableRule` still answers for the task catalog, whose two uses really are single
 * static conditions. The project-status grain now publishes its four reasons once for the whole
 * catalog (`notApplicableRules`) and sends WHICH one fired on the row, so its fields carry a null
 * rule and a per-field read alone would report every one of them as always-applicable.
 *
 * That mattered the moment it changed: `operatorsForField` withholds the exclude families from a
 * field this is true of, because the query engine treats an absent value as no-match in BOTH
 * directions, so a negation silently drops every not-applicable row alongside the ones the reader
 * meant to exclude - and for Retainer over/under, the one filterable field of the six, that is most
 * of a portfolio. */
export function mayBeNotApplicable(taxonomy: ReportTaxonomy, field: ReportTaxonomyField): boolean {
	if (field.notApplicableRule != null) {
		return true;
	}
	return taxonomy.notApplicableRules.length > 0 && ROW_REASON_NOT_APPLICABLE_KEYS.includes(field.key);
}

export function operatorLabel(taxonomy: ReportTaxonomy, op: string): string {
	return taxonomy.operators.find(o => o.value === op)?.label ?? op;
}

export function fieldByKey(taxonomy: ReportTaxonomy, key: string): ReportTaxonomyField | undefined {
	return taxonomy.fields.find(f => f.key === key);
}

/** Looks up a field by its `filterField` enum (STATUS, PROJECT, LABELS, ...). Filter conditions
 * key on the filterField, NOT the taxonomy `key` (`task.status.name`) that columns/grouping use —
 * the two vocabularies coexist by design, so a condition must resolve its field through here. */
export function fieldByFilterField(
	taxonomy: ReportTaxonomy,
	filterField: string | null | undefined
): ReportTaxonomyField | undefined {
	// Guard against a null/empty lookup key: several taxonomy fields carry `filterField: null`
	// (non-filterable derived fields), so a `=== null` match would silently return the first of
	// those instead of "no match". A condition can only reference a field by a real filterField.
	if (!filterField) {
		return undefined;
	}
	return taxonomy.fields.find(f => f.filterField === filterField);
}

/** Remaining work is filtered through a Yes/No control rather than a number entry, so the builder
 * needs a kind for it. It is deliberately NOT a `valueResolvers` entry in the taxonomy: that map
 * means "resolve these values as live entities", and the authoring agent sends every kind it finds
 * there to entity resolution, where a presentation-only kind would fail. Presentation is the
 * client's business, so the mapping lives here. */
const PRESENTATION_KINDS: Record<string, string> = {REMAINING: 'remainingWork'};

/** Kind for a field whose candidate values the catalog publishes outright. Like the presentation
 * kinds it is deliberately not a `valueResolvers` entry - those mean "resolve these values as live
 * entities" and the authoring agent sends every kind it finds there to entity resolution, which a
 * closed vocabulary of labels would fail. The list IS the resolution, so the client builds the
 * picker straight from it and can never offer a value the engine cannot apply.
 *
 * Being generic, this opens EVERY field that publishes a vocabulary, not a chosen few. On the
 * project-status catalog that is six: Progress, Retainer over/under, Status (RAG), Stage, Budget
 * type and Win %. Opening all of them is a decision, not an oversight - the ones asked for cannot
 * be singled out without an allowlist, and an allowlist is the hand-maintained copy of the catalog
 * this kind exists to avoid.
 *
 * Status (RAG), Stage and Budget type have changed both surfaces at once: their columns render a
 * display vocabulary rather than the wire token, so `enumeratedCandidates` relabels their picker
 * values through it. The candidate id stays the wire value either way, so what a condition stores is
 * unchanged. Budget type is the one whose two surfaces deliberately differ in wording - the picker
 * uses the standard report's plural "Retainers", the column a project's singular "Retainer" - and
 * whose picker folds two stored values (`FIXED_PRICE`, `FIXED_PRICE_V2`) into one Fixed Price option;
 * see `projectBudgetTypeLabels`. */
export const ENUMERATED_VALUES_KIND = 'enumeratedValues';

export function resolverKindFor(taxonomy: ReportTaxonomy, field: ReportTaxonomyField): string | undefined {
	const filterField = field.filterField;
	return (
		(filterField ? taxonomy.valueResolvers[filterField] : undefined) ??
		taxonomy.valueResolvers[field.key] ??
		(filterField ? PRESENTATION_KINDS[filterField] : undefined) ??
		// Last, not first: an explicit resolver entry and a hand-declared presentation control both
		// describe this field specifically, and either one outranks the generic fallback. An empty
		// list is no vocabulary at all - resolving a kind for one would open a picker with nothing in
		// it, telling the reader the field is filterable and then giving them no way to filter.
		(field.values && field.values.length > 0 ? ENUMERATED_VALUES_KIND : undefined)
	);
}

/** Resolver kinds the client can back with a usable, company-wide value source (the relay-backed
 * pickers plus the workflow-status categories in `useValueSource`'s registry). A categorical field
 * is only value-editable when its resolver kind is one of these. Keep this set in sync with the
 * editable entries of `conditions/valueSources/useValueSource`'s registry.
 *
 * `enumeratedValues` is the one entry with no registry row of its own: its candidates come from the
 * field being asked about rather than from a fixed list, so `useValueSource` builds it per field
 * from `field.values` instead of looking it up.
 *
 * `workflowStatus` belongs here even though status COLUMNS are per-project: the status filter is
 * matched against a task's status category, whose vocabulary is the same three values company-wide,
 * so the picker has a complete, unambiguous value set with no project scoping needed. */
const VALUE_EDITABLE_RESOLVER_KINDS = new Set([
	'project',
	'person',
	'client',
	'label',
	'role',
	'rateCard',
	'clientContact',
	'sprint',
	'workflowStatus',
	'remainingWork',
	ENUMERATED_VALUES_KIND,
	'freeText',
]);

export function isValueEditable(taxonomy: ReportTaxonomy, field: ReportTaxonomyField): boolean {
	// The taxonomy is what says which fields a reader may filter on, and this is the flag it says it
	// with. Read it first: a boolean field answers true below without consulting a resolver at all,
	// so a check placed after that would not cover one. Every non-filterable field in today's catalog
	// also carries `filterField: null`, which keeps it out of the add-condition list by a second
	// route - but the client must honour the declaration itself, not rely on that coincidence.
	if (!field.filterable) {
		return false;
	}
	if (field.dataType === 'boolean') {
		return true;
	}
	const kind = resolverKindFor(taxonomy, field);
	if (kind === undefined || !VALUE_EDITABLE_RESOLVER_KINDS.has(kind)) {
		return false;
	}
	// A non-categorical field is only editable through a control that stands in for the comparison,
	// which is what the presentation kinds are. An id-backed kind on a non-categorical field would
	// be a taxonomy mistake, so it stays excluded.
	if (field.categorical || (field.filterField !== null && PRESENTATION_KINDS[field.filterField] === kind)) {
		return true;
	}
	// A company custom field of type number, date or text has no candidate list either, and is not a
	// hand-declared presentation control: the catalog declares it free-text because its value is
	// typed in, so that declaration is what makes it editable.
	return (
		field.custom === true &&
		kind === 'freeText' &&
		(field.dataType === 'number' || field.dataType === 'date' || field.dataType === 'string')
	);
}
